import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmployeeMasterDataForm } from "@/components/EmployeeMasterDataForm";
import { EmployeeSalaryTable } from "@/components/EmployeeSalaryTable";
import { EmployeeBonusTable } from "@/components/EmployeeBonusTable";
import { EmployeeBenefitsTable } from "@/components/EmployeeBenefitsTable";
import { EmployeeVehicleCostsTab } from "@/components/EmployeeVehicleCostsTab";
import { EmployeeOtherCostsTab } from "@/components/EmployeeOtherCostsTab";
import { JobDescriptionForm } from "@/components/JobDescriptionForm";
import { EmployeeCredentialsForm } from "@/components/EmployeeCredentialsForm";
import { EmployeeDocumentsTable } from "@/components/EmployeeDocumentsTable";
import { EmployeeClothingForm } from "@/components/EmployeeClothingForm";
import { EmployeeToolsForm } from "@/components/EmployeeToolsForm";
import { EmployeeMobilePhoneTab } from "@/components/EmployeeMobilePhoneTab";
import { EmployeeTimeTrackingForm } from "@/components/EmployeeTimeTrackingForm";
import { EmployeeEvaluationHistoryTab } from "@/components/EmployeeEvaluationHistoryTab";
import { EmployeePerksTab } from "@/components/EmployeePerksTab";
import { EmployeeVehicleTasksTab } from "@/components/EmployeeVehicleTasksTab";
import { EmployeeConversationsTab } from "@/components/EmployeeConversationsTab";
import EmployeeDashboard from "@/components/EmployeeDashboard";
import { EmailCorrespondenceTab } from "@/components/EmailCorrespondenceTab";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Menu, Bell, Loader2, ChevronDown, Package, Wallet, Shirt, Wrench, Cog, DollarSign, Gift, TrendingUp, ClipboardList, Car, Receipt, Smartphone, Heart, Camera, MessageSquare, LayoutDashboard, ArrowLeft, Mail } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useEmployeeNotification, NotificationSettings, sendEmployeeNotificationWithChanges } from "@/hooks/useEmployeeNotification";
import { fetchEmployeeChanges, GroupedChanges, ChangeEntry } from "@/hooks/useEmployeeChanges";
import { NotificationChangesDialog } from "@/components/NotificationChangesDialog";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface EmployeeHRDetailProps {
  employeeId: string;
  employeeName: string;
}

// Define tab categories for grouped navigation
interface TabCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  tabs: TabItem[];
  employeeAccess: boolean;
}

interface TabItem {
  value: string;
  label: string;
  icon: React.ReactNode;
  employeeAccess: boolean;
}

// Individual tabs (not in categories) - in correct order
const standaloneTabs: TabItem[] = [
  { value: "dashboard", label: "Mein Überblick", icon: <LayoutDashboard className="h-4 w-4" />, employeeAccess: true },
  { value: "evaluations", label: "Bewertung", icon: <ClipboardList className="h-4 w-4" />, employeeAccess: true },
  { value: "conversations", label: "Mitarbeitergespräche", icon: <MessageSquare className="h-4 w-4" />, employeeAccess: true },
  { value: "emails", label: "E-Mails", icon: <Mail className="h-4 w-4" />, employeeAccess: false },
  { value: "credentials", label: "Zugangsdaten", icon: null, employeeAccess: true },
];

// Grouped tab categories - in correct order
const tabCategories: TabCategory[] = [
  {
    id: "masterdata",
    label: "Stammdaten",
    icon: <Package className="h-4 w-4" />,
    employeeAccess: true,
    tabs: [
      { value: "masterdata", label: "Stammdaten", icon: null, employeeAccess: true },
      { value: "jobdesc", label: "Stellenbeschreibung", icon: null, employeeAccess: true },
      { value: "documents", label: "Dokumente", icon: null, employeeAccess: true },
      { value: "timetracking", label: "Stunden/Abwesenheit", icon: null, employeeAccess: true },
    ],
  },
  {
    id: "equipment",
    label: "Betriebsausstattung",
    icon: <Package className="h-4 w-4" />,
    employeeAccess: true,
    tabs: [
      { value: "clothing", label: "Arbeitskleidung", icon: <Shirt className="h-4 w-4" />, employeeAccess: true },
      { value: "tools", label: "Werkzeug", icon: <Wrench className="h-4 w-4" />, employeeAccess: true },
      { value: "mobile-phone", label: "Handy", icon: <Smartphone className="h-4 w-4" />, employeeAccess: true },
    ],
  },
  {
    id: "personnel-costs",
    label: "Personalkosten",
    icon: <Wallet className="h-4 w-4" />,
    employeeAccess: true,
    tabs: [
      { value: "salary", label: "Gehalt", icon: <DollarSign className="h-4 w-4" />, employeeAccess: true },
      { value: "bonus", label: "Bonus", icon: <TrendingUp className="h-4 w-4" />, employeeAccess: true },
      { value: "benefits", label: "Benefits", icon: <Gift className="h-4 w-4" />, employeeAccess: true },
      { value: "perks", label: "Annehmlichkeiten", icon: <Heart className="h-4 w-4" />, employeeAccess: true },
    ],
  },
  {
    id: "other-costs",
    label: "Sonstige Kosten",
    icon: <Receipt className="h-4 w-4" />,
    employeeAccess: true,
    tabs: [
      { value: "vehicle-tasks", label: "Fahrzeug-Aufgaben", icon: <Camera className="h-4 w-4" />, employeeAccess: true },
      { value: "vehicle-costs", label: "KFZ-Kosten", icon: <Car className="h-4 w-4" />, employeeAccess: true },
      { value: "other-costs", label: "Sonstige Kosten", icon: <Receipt className="h-4 w-4" />, employeeAccess: true },
    ],
  },
];

// Legacy allTabs for notification mapping compatibility
const allTabs = [
  { value: "masterdata", label: "Stammdaten", employeeAccess: true },
  { value: "jobdesc", label: "Stellenbeschreibung", employeeAccess: true },
  { value: "documents", label: "Dokumente", employeeAccess: true },
  { value: "timetracking", label: "Stunden/Abwesenheit", employeeAccess: true },
  { value: "clothing", label: "Arbeitskleidung", employeeAccess: true },
  { value: "tools", label: "Werkzeug", employeeAccess: true },
  { value: "mobile-phone", label: "Handy", employeeAccess: true },
  { value: "salary", label: "Gehalt", employeeAccess: true },
  { value: "bonus", label: "Bonus", employeeAccess: true },
  { value: "benefits", label: "Benefits", employeeAccess: true },
  { value: "vehicle-tasks", label: "Fahrzeug-Aufgaben", employeeAccess: true },
  { value: "vehicle-costs", label: "KFZ-Kosten", employeeAccess: true },
  { value: "other-costs", label: "Sonstige Kosten", employeeAccess: true },
  { value: "evaluations", label: "Bewertung", employeeAccess: true },
  { value: "conversations", label: "Mitarbeitergespräche", employeeAccess: true },
  { value: "perks", label: "Annehmlichkeiten", employeeAccess: true },
  { value: "credentials", label: "Zugangsdaten", employeeAccess: true },
];

export function EmployeeHRDetail({ employeeId, employeeName }: EmployeeHRDetailProps) {
  const isMobile = useIsMobile();
  const { profile, userRole } = useAuth();
  const { checkPermission, isAdmin, isSelfContext } = usePermissions();
  const isEmployee = userRole === 'employee';
  const isOwnProfile = profile?.employee_id === employeeId;
  const [excludeFromEvaluation, setExcludeFromEvaluation] = useState(false);
  const [showEvaluationDashboard, setShowEvaluationDashboard] = useState(true);
  
  // Mobile navigation state: null = show category overview, string = show tabs within category
  const [mobileActiveCategory, setMobileActiveCategory] = useState<string | null>(null);
  
  // Load employee evaluation settings
  useEffect(() => {
    const loadEvaluationSettings = async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("exclude_from_evaluation, show_evaluation_dashboard")
        .eq("id", employeeId)
        .single();
      
      if (!error && data) {
        setExcludeFromEvaluation(data.exclude_from_evaluation || false);
        setShowEvaluationDashboard(data.show_evaluation_dashboard !== false); // Default true
      }
    };
    loadEvaluationSettings();
  }, [employeeId]);
  
  // Für Mitarbeiter, die ihre eigenen Daten ansehen: Dashboard als Start-Tab (wenn nicht ausgeschlossen)
  const [activeTab, setActiveTab] = useState(() => {
    if (isEmployee && isOwnProfile) return "dashboard";
    return "masterdata";
  });
  
  // Wenn excludeFromEvaluation true oder showEvaluationDashboard false, wechsle zu masterdata
  useEffect(() => {
    if ((excludeFromEvaluation || !showEvaluationDashboard) && activeTab === "dashboard") {
      setActiveTab("masterdata");
    }
  }, [excludeFromEvaluation, showEvaluationDashboard, activeTab]);
  
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>(["masterdata", "equipment", "personnel-costs"]);
  const [activeCategory, setActiveCategory] = useState<string | null>("masterdata");
  const [monthlyAverageHours, setMonthlyAverageHours] = useState<number | undefined>(undefined);
  const [employeeEmail, setEmployeeEmail] = useState<string | undefined>(undefined);
  const [pendingChangesCount, setPendingChangesCount] = useState<number>(0);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<ChangeEntry[]>([]);
  const [groupedChanges, setGroupedChanges] = useState<GroupedChanges>({});

  // Lade Anzahl der ausstehenden Änderungen
  const loadPendingChanges = useCallback(async () => {
    const { changes, grouped } = await fetchEmployeeChanges(employeeId);
    setPendingChangesCount(changes.length);
    setPendingChanges(changes);
    setGroupedChanges(grouped);
  }, [employeeId]);

  useEffect(() => {
    loadPendingChanges();
  }, [loadPendingChanges]);

  // Lade monatliche Durchschnittsstunden für Stundenlohn-Berechnung
  useEffect(() => {
    const loadEmployeeHours = async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("monthly_average_hours, business_email")
        .eq("id", employeeId)
        .single();

      if (!error && data) {
        setMonthlyAverageHours(data.monthly_average_hours || undefined);
        setEmployeeEmail(data.business_email || undefined);
      }
    };
    loadEmployeeHours();
  }, [employeeId]);
  
  const canManage = isAdmin || userRole === 'hr_manager';

  // Map tab values to scopes (gemäß neuer Spezifikation)
  const tabScopeMap: Record<string, string> = {
    masterdata: "masterdata",
    jobdesc: "job_description",
    documents: "documents",
    timetracking: "time_absence",
    clothing: "equipment",
    tools: "equipment",
    "mobile-phone": "equipment",
    salary: "payroll",
    bonus: "payroll",
    benefits: "payroll",
    "vehicle-tasks": "kfz_images",
    "vehicle-costs": "other_costs",
    "ev-charging": "other_costs",
    "other-costs": "other_costs",
    evaluations: "ratings",
    conversations: "ratings",
    perks: "payroll",
    credentials: "credentials",
  };

  // Berechtigungsprüfung mit SELF/OTHERS-Logik
  const effectiveCanRead = (scope: string): boolean => {
    // Admin: immer Zugriff
    if (isAdmin) return true;
    
    // Für eigene Daten: immer lesbar (SELF-Baseline)
    if (isOwnProfile) return true;
    
    // Für andere: Matrix prüfen
    return checkPermission(scope as any, "read", employeeId);
  };

  // KRITISCH: Effektive ReadOnly-Berechnung
  // - Bei eigenem Profil (SELF): IMMER read-only (Self-Baseline erlaubt kein Bearbeiten)
  // - Bei fremdem Profil (OTHERS): read-only wenn kein Edit-Recht in der Rollenmatrix
  const effectiveReadOnly = (scope: string): boolean => {
    // Admin: nie read-only
    if (isAdmin) return false;
    
    if (isOwnProfile) {
      // Self-Baseline: Benutzer kann eigene Daten nur lesen, nicht bearbeiten
      // Ausnahmen: documents, kfz_images, credentials haben Self-Write-Rechte
      if (["documents", "kfz_images", "credentials"].includes(scope)) {
        return false; // Diese dürfen bearbeitet werden
      }
      return true;
    }
    // OTHERS-Kontext: Prüfe Edit-Berechtigung aus der Rollenmatrix
    return !checkPermission(scope as any, "write", employeeId);
  };

  // Filter standalone tabs based on permissions (mit Self-Service-Logik)
  // Dashboard nur für Mitarbeiter anzeigen, die ihr eigenes Profil ansehen, 
  // nicht von Bewertung ausgeschlossen sind UND Dashboard-Anzeige aktiviert ist
  const filteredStandaloneTabs = standaloneTabs.filter(tab => {
    // Dashboard nur anzeigen wenn: eigenes Profil, nicht ausgeschlossen, Dashboard aktiviert
    if (tab.value === "dashboard") {
      return isOwnProfile && !excludeFromEvaluation && showEvaluationDashboard;
    }
    if (tab.value === "evaluations") {
      return !excludeFromEvaluation && effectiveCanRead(tabScopeMap[tab.value] || "masterdata");
    }
    if (tab.value === "emails") {
      // E-Mail-Verlauf nur für Admin und HR-Manager
      return isAdmin || userRole === "hr_manager";
    }
    return effectiveCanRead(tabScopeMap[tab.value] || "masterdata");
  });

  // Filter categories and their tabs based on permissions (mit Self-Service-Logik)
  // NEUE LOGIK: Kategorie anzeigen, wenn mindestens ein Tab darin Leserecht hat
  // Dies erlaubt z.B. Mitarbeitern Gehalt/Bonus/Benefits zu sehen, auch wenn das Parent-Modul false ist
  const filteredCategories = tabCategories
    .map(category => {
      // Zuerst alle Tabs filtern, die Leserecht haben
      const filteredTabs = category.tabs.filter(tab => {
        const tabScope = tabScopeMap[tab.value] || "masterdata";
        return effectiveCanRead(tabScope);
      });

      return {
        ...category,
        tabs: filteredTabs,
      };
    })
    // Kategorie nur anzeigen, wenn mindestens ein Tab übrig bleibt
    .filter(category => category.tabs.length > 0);

  const handleTabChange = (value: string, categoryId?: string) => {
    setActiveTab(value);
    if (categoryId) {
      setActiveCategory(categoryId);
      setMobileActiveCategory(categoryId);
    } else {
      setActiveCategory(null);
      setMobileActiveCategory(null);
    }
    setSheetOpen(false);
  };

  const handleCategoryClick = (categoryId: string, firstTabValue: string) => {
    setActiveCategory(categoryId);
    setActiveTab(firstTabValue);
    setMobileActiveCategory(categoryId);
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const categoryMapping: Record<string, keyof NotificationSettings["categories"]> = {
    masterdata: "stammdaten",
    timetracking: "stunden_abwesenheit",
    evaluations: "bewertung",
    clothing: "arbeitskleidung",
    tools: "werkzeug",
    "mobile-phone": "handy",
    jobdesc: "stellenbeschreibung",
    credentials: "zugangsdaten",
    documents: "dokumente",
    salary: "gehalt",
    bonus: "bonus",
    benefits: "benefits",
    "vehicle-tasks": "kfz_kosten",
    "vehicle-costs": "kfz_kosten",
    "other-costs": "sonstige_kosten",
    costs: "sonstige_kosten",
    perks: "annehmlichkeiten",
    conversations: "mitarbeitergespraech",
  };

  const handleOpenNotificationDialog = () => {
    setNotificationDialogOpen(true);
  };

  const handleSendSelectedChanges = async (selectedChanges: GroupedChanges) => {
    setSendingNotification(true);
    const result = await sendEmployeeNotificationWithChanges(
      employeeId, 
      profile?.full_name || "System", 
      selectedChanges
    );
    setSendingNotification(false);
    setNotificationDialogOpen(false);
    
    if (result.success) {
      // Nach dem Senden die Anzahl aktualisieren
      await loadPendingChanges();
    }
  };

  const getCurrentTabLabel = () => {
    // Check standalone tabs
    const standalone = standaloneTabs.find(t => t.value === activeTab);
    if (standalone) return standalone.label;
    
    // Check category tabs
    for (const category of tabCategories) {
      const tab = category.tabs.find(t => t.value === activeTab);
      if (tab) return `${category.label} > ${tab.label}`;
    }
    return "Stammdaten";
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "dashboard":
        return <EmployeeDashboard employeeId={employeeId} employeeName={employeeName} onNavigateToEvaluations={() => setActiveTab("evaluations")} />;
      case "masterdata":
        return <EmployeeMasterDataForm employeeId={employeeId} readOnly={effectiveReadOnly("masterdata")} />;
      case "timetracking":
        return <EmployeeTimeTrackingForm employeeId={employeeId} employeeName={employeeName} readOnly={effectiveReadOnly("time_absence")} />;
      case "evaluations":
        return <div id="evaluation-history"><EmployeeEvaluationHistoryTab employeeId={employeeId} employeeName={employeeName} /></div>;
      case "clothing":
        return <EmployeeClothingForm employeeId={employeeId} employeeName={employeeName} readOnly={effectiveReadOnly("equipment")} />;
      case "tools":
        return <EmployeeToolsForm employeeId={employeeId} employeeName={employeeName} readOnly={effectiveReadOnly("equipment")} />;
      case "mobile-phone":
        return <EmployeeMobilePhoneTab employeeId={employeeId} employeeName={employeeName} readOnly={effectiveReadOnly("equipment")} />;
      case "jobdesc":
        return <JobDescriptionForm employeeId={employeeId} readOnly={effectiveReadOnly("job_description")} />;
      case "credentials":
        return <EmployeeCredentialsForm employeeId={employeeId} readOnly={effectiveReadOnly("credentials")} />;
      case "documents":
        return <EmployeeDocumentsTable employeeId={employeeId} readOnly={effectiveReadOnly("documents")} />;
      case "salary":
        return <EmployeeSalaryTable employeeId={employeeId} employeeName={employeeName} readOnly={effectiveReadOnly("payroll")} canViewData={isAdmin || isOwnProfile} monthlyAverageHours={monthlyAverageHours} />;
      case "bonus":
        return <EmployeeBonusTable employeeId={employeeId} employeeName={employeeName} readOnly={effectiveReadOnly("payroll")} canViewData={isAdmin || isOwnProfile} />;
      case "benefits":
        return <EmployeeBenefitsTable employeeId={employeeId} employeeName={employeeName} readOnly={effectiveReadOnly("payroll")} canViewData={isAdmin || isOwnProfile} />;
      case "vehicle-tasks":
        return <EmployeeVehicleTasksTab employeeId={employeeId} employeeName={employeeName} />;
      case "vehicle-costs":
        return <EmployeeVehicleCostsTab employeeId={employeeId} employeeName={employeeName} readOnly={effectiveReadOnly("other_costs")} />;
      case "other-costs":
        return <EmployeeOtherCostsTab employeeId={employeeId} employeeName={employeeName} readOnly={effectiveReadOnly("other_costs")} />;
      case "perks":
        return <EmployeePerksTab employeeId={employeeId} readOnly={effectiveReadOnly("payroll")} />;
      case "conversations":
        return <EmployeeConversationsTab employeeId={employeeId} employeeName={employeeName} readOnly={effectiveReadOnly("ratings")} />;
      case "emails":
        return <EmailCorrespondenceTab employeeId={employeeId} defaultEmail={employeeEmail} recipientName={employeeName} />;
      default:
        return null;
    }
  };

  const renderMobileMenu = () => (
    <div className="space-y-2">
      {/* All Categories in order */}
      {filteredCategories.map((category) => (
        <Collapsible
          key={category.id}
          open={expandedCategories.includes(category.id)}
          onOpenChange={() => toggleCategory(category.id)}
        >
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between font-semibold">
              <span className="flex items-center gap-2">
                {category.icon}
                {category.label}
              </span>
              <ChevronDown className={cn(
                "h-4 w-4 transition-transform",
                expandedCategories.includes(category.id) && "rotate-180"
              )} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pl-4 space-y-1">
            {category.tabs.map((tab) => (
              <Button
                key={tab.value}
                variant={activeTab === tab.value ? "secondary" : "ghost"}
                className="w-full justify-start gap-2"
                onClick={() => handleTabChange(tab.value, category.id)}
              >
                {tab.icon}
                {tab.label}
              </Button>
            ))}
          </CollapsibleContent>
        </Collapsible>
      ))}

      {/* Standalone tabs */}
      {filteredStandaloneTabs.map((tab) => (
        <Button
          key={tab.value}
          variant={activeTab === tab.value ? "secondary" : "ghost"}
          className="w-full justify-start"
          onClick={() => handleTabChange(tab.value)}
        >
          {tab.label}
        </Button>
      ))}
    </div>
  );

  const isInCategory = (tabValue: string, categoryId: string) => {
    const category = tabCategories.find(c => c.id === categoryId);
    return category?.tabs.some(t => t.value === tabValue) || false;
  };

  const getActiveCategoryForTab = (tabValue: string): string | null => {
    for (const category of tabCategories) {
      if (category.tabs.some(t => t.value === tabValue)) {
        return category.id;
      }
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {canManage && (
        <div className="flex justify-end">
          <Button 
            variant="outline" 
            size="sm" 
            disabled={sendingNotification || pendingChangesCount === 0}
            onClick={handleOpenNotificationDialog}
            className="relative"
          >
            <Bell className="h-4 w-4 mr-2" />
            Änderungen mitteilen
            {pendingChangesCount > 0 && (
              <Badge 
                variant="destructive" 
                className="ml-2 h-5 min-w-5 px-1.5 text-xs"
              >
                {pendingChangesCount}
              </Badge>
            )}
          </Button>
        </div>
      )}

      <NotificationChangesDialog
        open={notificationDialogOpen}
        onOpenChange={setNotificationDialogOpen}
        changes={pendingChanges}
        grouped={groupedChanges}
        onSend={handleSendSelectedChanges}
        isSending={sendingNotification}
      />

      {isMobile ? (
        // Mobile: Category-based navigation (no Sheet)
        <div className="space-y-4">
          {mobileActiveCategory === null ? (
            // Show category overview + standalone tabs
            <div className="space-y-2">
              {/* Standalone tabs first */}
              {filteredStandaloneTabs.map((tab) => (
                <Button
                  key={tab.value}
                  variant={activeTab === tab.value ? "secondary" : "outline"}
                  className="w-full justify-start gap-2 h-12"
                  onClick={() => {
                    setActiveTab(tab.value);
                    setActiveCategory(null);
                    setMobileActiveCategory(null);
                  }}
                >
                  {tab.icon}
                  {tab.label}
                </Button>
              ))}
              
              {/* Categories */}
              {filteredCategories.map((category) => (
                <Button
                  key={category.id}
                  variant="outline"
                  className="w-full justify-between h-12"
                  onClick={() => {
                    setMobileActiveCategory(category.id);
                    setActiveCategory(category.id);
                    setActiveTab(category.tabs[0]?.value);
                  }}
                >
                  <span className="flex items-center gap-2">
                    {category.icon}
                    {category.label}
                  </span>
                  <ChevronDown className="h-4 w-4 -rotate-90" />
                </Button>
              ))}
            </div>
          ) : (
            // Show tabs within selected category with back button
            <div className="space-y-2">
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 text-muted-foreground mb-2"
                onClick={() => {
                  setMobileActiveCategory(null);
                }}
              >
                <ArrowLeft className="h-4 w-4" />
                Zurück zur Übersicht
              </Button>
              
              {/* Category label */}
              {(() => {
                const cat = filteredCategories.find(c => c.id === mobileActiveCategory);
                if (!cat) return null;
                return (
                  <>
                    <p className="text-sm font-semibold text-muted-foreground px-3 mb-1">{cat.label}</p>
                    {cat.tabs.map((tab) => (
                      <Button
                        key={tab.value}
                        variant={activeTab === tab.value ? "secondary" : "ghost"}
                        className="w-full justify-start gap-2 h-11"
                        onClick={() => {
                          setActiveTab(tab.value);
                          setActiveCategory(cat.id);
                        }}
                      >
                        {tab.icon}
                        {tab.label}
                      </Button>
                    ))}
                  </>
                );
              })()}
            </div>
          )}
          
          {/* Only show content when a tab is selected (standalone or within category) */}
          {(mobileActiveCategory !== null || filteredStandaloneTabs.some(t => t.value === activeTab)) && renderTabContent()}
        </div>
      ) : (
        // Desktop: Structured layout with main categories and sub-tabs
        <div className="space-y-4">
          {/* Main Navigation - Categories and Standalone Tabs */}
          <div className="flex flex-wrap gap-2 p-2 bg-muted/50 rounded-lg">
            {/* Categories as main navigation items */}
            {filteredCategories.map((category) => (
              <Button
                key={category.id}
                variant={activeCategory === category.id || isInCategory(activeTab, category.id) ? "secondary" : "ghost"}
                size="sm"
                onClick={() => handleCategoryClick(category.id, category.tabs[0]?.value)}
                className="text-xs sm:text-sm gap-1"
              >
                {category.icon}
                {category.label}
              </Button>
            ))}

            {/* Standalone tabs */}
            {filteredStandaloneTabs.map((tab) => (
              <Button
                key={tab.value}
                variant={activeTab === tab.value ? "secondary" : "ghost"}
                size="sm"
                onClick={() => handleTabChange(tab.value)}
                className="text-xs sm:text-sm"
              >
                {tab.label}
              </Button>
            ))}
          </div>

          {/* Sub-Navigation Tabs - shown when a category is active */}
          {filteredCategories.map((category) => (
            (activeCategory === category.id || isInCategory(activeTab, category.id)) && (
              <div key={category.id} className="flex flex-wrap gap-2 p-2 bg-accent/30 rounded-lg border-l-4 border-l-primary">
                {category.tabs.map((tab) => (
                  <Button
                    key={tab.value}
                    variant={activeTab === tab.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleTabChange(tab.value, category.id)}
                    className="gap-2"
                  >
                    {tab.icon}
                    {tab.label}
                  </Button>
                ))}
              </div>
            )
          ))}

          {/* Tab Content */}
          {renderTabContent()}
        </div>
      )}
    </div>
  );
}
